/** @fileoverview Number formats, axis steps, colors and plain messages shared by the chart blocks. */

import type {
  FabrikChartComponent,
  FabrikChartData,
  FabrikChartErrorCode,
  FabrikComponentSize,
} from '../../types/apps';

/** Key the server gives the merged rest of the categories or series. */
export const OTHER_KEY = '__other__';

/** Key of the only series of a chart that is not split. */
const SINGLE_SERIES_KEY = '__all__';

/* Split series and donut slices take a fixed, tested set, so neighbours never share a hue. */
const SERIES_COLORS = [
  'var(--fabrik-chart-1)',
  'var(--fabrik-chart-2)',
  'var(--fabrik-chart-3)',
  'var(--fabrik-chart-4)',
  'var(--fabrik-chart-5)',
  'var(--fabrik-chart-6)',
];

/**
 * Color of one series or donut slice: the app's primary color for a chart that is not split,
 * grey for Other, else the fixed order by position.
 * @param key the series or category key
 * @param position its place in the server's order
 */
export const chartColorOf = (key: string, position: number): string => {
  if (key === SINGLE_SERIES_KEY) {
    return 'var(--primary)';
  }
  return key === OTHER_KEY
    ? 'var(--fabrik-chart-other)'
    : SERIES_COLORS[position % SERIES_COLORS.length];
};

/** Plain message for each error code the server can send for one chart. */
export const CHART_ERROR_TEXT: Record<FabrikChartErrorCode, string> = {
  TOO_MANY_ROWS: 'This chart covers more than 10,000 rows. Narrow the filters and try again.',
  OVER_BUDGET: 'This screen has too many charts to load at once.',
  TIMEOUT: 'This chart took too long to load. Try again or narrow the filters.',
  QUERY_FAILED: 'The data for this chart could not be read.',
};

/**
 * Formats an exact value for a tooltip, legend or table: whole counts, sums with up to 2
 * decimals, averages with at most 1.
 * @param value the value, or null for an average with no values
 * @param measure the chart's measure
 */
export const formatExact = (
  value: number | null,
  measure: FabrikChartComponent['measure']
): string => {
  if (value === null) {
    return 'No value';
  }
  const maximumFractionDigits =
    !measure || measure.fn === 'count' ? 0 : measure.fn === 'sum' ? 2 : 1;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value);
};

/**
 * Formats an axis step or a big total short: 12.5K for large numbers, plain below 10,000.
 * @param value the number to show
 */
export const formatCompact = (value: number): string =>
  Math.abs(value) < 10_000
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)
    : new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
        value
      );

/**
 * Formats a share of the total as a whole or one-decimal percent.
 * @param share the part between 0 and 1
 */
export const formatPercent = (share: number): string =>
  new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }).format(share);

/**
 * Picks round axis steps that cover the values, such as 0, 500, 1,000, 1,500.
 * Always includes 0, so bars grow from the baseline.
 * @param low the smallest value to show
 * @param high the largest value to show
 */
export const niceTicks = (low: number, high: number): number[] => {
  const bottom = Math.min(0, low);
  const top = Math.max(0, high);
  if (bottom === top) {
    return [0, 1];
  }
  const roughStep = (top - bottom) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const step =
    [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((value) => value >= roughStep) ??
    10 * magnitude;
  const first = Math.floor(bottom / step) * step;
  const last = Math.ceil(top / step) * step;
  const ticks: number[] = [];
  for (let tick = first; tick <= last + step / 2; tick += step) {
    /* Rounding keeps steps like 0.1 + 0.2 from showing as 0.30000000000000004. */
    ticks.push(Number(tick.toPrecision(12)));
  }
  return ticks;
};

/* Plot heights in pixels for each component size step; md is the usual look. */
const PLOT_HEIGHTS: Record<FabrikComponentSize, number> = { sm: 180, md: 240, lg: 300, xl: 360 };

/**
 * Height of the plot area for a chart size step.
 * @param size the chart's size step, md when left out
 */
export const plotHeightFor = (size: FabrikComponentSize = 'md'): number => PLOT_HEIGHTS[size];

/**
 * Short note when the server merged the rest of the categories or series into Other, or kept only
 * the latest line steps. Empty when nothing was cut.
 * @param data the chart's numbers
 * @param type the chart type
 */
export const truncatedNote = (
  data: FabrikChartData,
  type: FabrikChartComponent['type']
): string => {
  const { truncated, categories, series } = data;
  const notes: string[] = [];
  if (truncated.categories) {
    notes.push(
      type === 'line'
        ? `Showing the latest ${categories.length}.`
        : `Showing the top ${type === 'donut' ? 6 : 20}. The rest are in Other.`
    );
  }
  if (truncated.series) {
    const shown = series.filter(({ key }) => key !== OTHER_KEY).length;
    notes.push(`Showing ${shown} groups by size. The rest are in Other.`);
  }
  return notes.join(' ');
};

/**
 * Value of one cell, with null as 0 for drawing.
 * @param data the chart's numbers
 * @param seriesIndex the series position
 * @param categoryIndex the category position
 */
export const drawnValue = (
  data: FabrikChartData,
  seriesIndex: number,
  categoryIndex: number
): number => data.values[seriesIndex]?.[categoryIndex] ?? 0;
